import React from 'react';
import Select, { ValueType } from 'react-select';
import { ChevronRightIcon } from '../../../../../ui/Icons';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import { ReservationDetailsUiModel } from '../../../../../shared/ui-models/reservation.ui-model';
import { UserUIModel } from '../../../../../shared/ui-models/user.ui-model';
import { ErrorInfo, isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import { getSalesPersons } from '../../../../../SalesPersonsModule/data.service';
import { ReservationSource } from '../../../../../shared/enums/reservation-souce.enum';
import { ReservationChannel } from '../../../../../shared/enums/reservation-channel.enum';
import { ReservationTypeOfStay } from '../../../../../shared/enums/reservation-type-of-stay.enum';
import { BrokerUiModel } from '../../../../../shared/ui-models/broker.ui-model';
import { getAllBrokers } from '../../../../../shared/services/data/broker.data';
import {
  AttributionOption,
  MarketingChannel,
  marketingChannelOptions,
  MessagingChannel,
  messagingChannelOptions
} from '../../../../../shared/interfaces/attribution.interface';
import {
  getAssignableSourceOptions,
  getEditableSourceOptions,
  getReservationChannelLabel,
  getReservationSourceImage,
  getReservationSourceLabel,
  reservationChannelOptionsBySource,
  ReservationChannelOption,
  ReservationSourceOption
} from '../../../../../shared/services/helpers/reservation-source-channel.helper';

export type SourceChannelDraft = {
  source?: ReservationSource;
  channel?: ReservationChannel;
  salesPerson?: string;
  typeOfStay?: ReservationTypeOfStay;
  brokerId?: number;
  marketingChannel?: MarketingChannel;
  messagingChannel?: MessagingChannel;
};

type SegmentOption = { value: ReservationTypeOfStay; label: string };

const segmentOptions: SegmentOption[] = Object.values(ReservationTypeOfStay).map((value) => ({
  value,
  label: value.replaceAll('_', ' ').toUpperCase()
}));

type SalesPersonOption = {
  value: string;
  label: string;
  salesPerson: UserUIModel;
};

type BrokerOption = { value: number; label: string };

interface ReservationSourceChannelModalProps {
  data: {
    reservation: ReservationDetailsUiModel;
    showModal: boolean;
    sourceChannelDraft: SourceChannelDraft;
    sourceChannelSaving: boolean;
    disableSourceChannelFields: boolean;
  };
  eventHandlers: {
    onClose: () => void;
    onDraftChange: React.Dispatch<React.SetStateAction<SourceChannelDraft>>;
    onSave: () => void;
  };
}

const sourceChannelSelectMenuStyles = {
  control: (base: any) => ({ ...base, minHeight: 54 }),
  input: (base: any) => ({ ...base, fontSize: 18 }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  option: (base: any) => ({ ...base, fontSize: 18, lineHeight: '28px', padding: '14px 16px' }),
  placeholder: (base: any) => ({ ...base, fontSize: 18 }),
  singleValue: (base: any) => ({ ...base, fontSize: 18 }),
  valueContainer: (base: any) => ({ ...base, padding: '8px 12px' })
};

export const ReservationSourceChannelModal = ({
  data: {
    reservation,
    showModal,
    sourceChannelDraft,
    sourceChannelSaving,
    disableSourceChannelFields
  },
  eventHandlers: { onClose, onDraftChange, onSave }
}: ReservationSourceChannelModalProps) => {
  const [salesPersons, setSalesPersons] = React.useState<UserUIModel[]>([]);
  const [isSalesPersonsLoading, setIsSalesPersonsLoading] = React.useState(false);
  const [brokers, setBrokers] = React.useState<BrokerUiModel[]>([]);
  const [isBrokersLoading, setIsBrokersLoading] = React.useState(false);

  // Which extra fields this reservation can answer, on the same rules as the
  // create form: a broker only belongs to a BROKER stay, and marketing and
  // messaging are CX questions because CX is the team that takes the call and
  // can actually ask. Keyed on the draft, not the saved reservation, so the
  // pickers follow what is being picked rather than lagging a save behind.
  const draftTypeOfStay = sourceChannelDraft.typeOfStay;
  const isBrokerSegment = draftTypeOfStay === ReservationTypeOfStay.BROKER;
  const isCxSource = sourceChannelDraft.source === ReservationSource.CX;
  // Built from the reservation rather than module-level so a source that is no
  // longer assignable stays visible on the one reservation that already has it.
  const sourceOptions = React.useMemo(
    () => getEditableSourceOptions(reservation.source, draftTypeOfStay),
    [reservation.source, draftTypeOfStay]
  );
  const selectedSource = sourceOptions.find((option) => option.value === sourceChannelDraft.source);
  const availableChannelOptions = sourceChannelDraft.source
    ? reservationChannelOptionsBySource[sourceChannelDraft.source]
    : [];
  const selectedChannel = availableChannelOptions.find(
    (option) => option.value === sourceChannelDraft.channel
  );
  const currentSourceLabel = getReservationSourceLabel(reservation.source);
  const currentChannelLabel = getReservationChannelLabel(reservation.channel);
  const hasSourceChannelChanges =
    sourceChannelDraft.source !== reservation.source ||
    sourceChannelDraft.channel !== reservation.channel ||
    sourceChannelDraft.salesPerson !== reservation.salesPerson ||
    sourceChannelDraft.typeOfStay !== reservation.typeOfStay ||
    sourceChannelDraft.brokerId !== reservation.brokerId ||
    sourceChannelDraft.marketingChannel !== reservation.marketingChannel ||
    sourceChannelDraft.messagingChannel !== reservation.messagingChannel;
  const selectedSalesPerson = salesPersons.find(
    (salesPerson) => salesPerson.id === sourceChannelDraft.salesPerson
  );

  // Narrowed to brokers that actually have an id, as on the create form: the id
  // is optional on the model and an option without a value cannot be selected.
  const brokerOptions: BrokerOption[] = brokers
    .filter((broker): broker is BrokerUiModel & { id: number } => typeof broker.id === 'number')
    .map((broker) => ({ value: broker.id, label: broker.name || `Broker ${broker.id}` }));
  const selectedBroker = brokerOptions.find(
    (option) => option.value === sourceChannelDraft.brokerId
  );
  const selectedMarketingChannel =
    marketingChannelOptions.find(
      (option) => option.value === sourceChannelDraft.marketingChannel
    ) || null;
  const selectedMessagingChannel =
    messagingChannelOptions.find(
      (option) => option.value === sourceChannelDraft.messagingChannel
    ) || null;

  React.useEffect(() => {
    if (!showModal) return;

    let ignore = false;
    setIsSalesPersonsLoading(true);

    getSalesPersons()
      .then((response: UserUIModel[] | ErrorInfo) => {
        if (ignore) return;

        setSalesPersons(isErrorInfo(response) ? [] : response);
      })
      .finally(() => {
        if (!ignore) setIsSalesPersonsLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [showModal]);

  // Only fetched for a stay that can actually have a broker -- every other
  // reservation would pay for a request whose result it never shows.
  React.useEffect(() => {
    if (!showModal || !isBrokerSegment) return;

    let ignore = false;
    setIsBrokersLoading(true);

    getAllBrokers()
      .then((response: BrokerUiModel[] | ErrorInfo) => {
        if (ignore) return;

        setBrokers(isErrorInfo(response) ? [] : response);
      })
      .finally(() => {
        if (!ignore) setIsBrokersLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [showModal, isBrokerSegment]);

  return (
    <Modal
      config={{
        showModal,
        fullScreen: false,
        showClose: true,
        maxWidth: 'sm'
      }}
      eventHandlers={{ closeModalHandler: onClose }}
    >
      <div className="w-full rounded-xl bg-white">
        <div className="relative border-b border-blue-100 bg-gradient-to-br from-blue-50 via-white to-slate-50 px-5 py-5 pr-12 sm:px-6">
          <div className="absolute right-6 top-5 hidden size-14 rounded-full border border-blue-100 bg-white/70 shadow-sm sm:block" />
          <h2 className="m-0 text-4xl font-gilroy-medium text-gray-900">Edit source and channel</h2>
        </div>

        <div className="px-5 py-5 sm:px-6">
          <div className="mb-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50 to-white p-4 shadow-sm">
              <div className="mb-4 flex items-center justify-between gap-3">
                <p className="m-0 text-base font-semibold uppercase tracking-wider text-gray-500">
                  Current
                </p>
              </div>

              <div className="flex items-start gap-4">
                {reservation.source && (
                  <img
                    className="size-12 rounded-xl border border-white bg-white object-contain p-2 shadow-sm"
                    src={getReservationSourceImage(reservation.source)}
                    alt={currentSourceLabel}
                  />
                )}
                <div className="min-w-0 flex-1 space-y-3">
                  <div>
                    <p className="m-0 text-sm font-semibold uppercase tracking-wide text-gray-400">
                      Source
                    </p>
                    <p className="mt-1 mb-0 truncate text-xl font-gilroy-medium text-gray-900">
                      {currentSourceLabel || 'No source'}
                    </p>
                  </div>
                  <div className="border-t border-gray-200 pt-3">
                    <p className="m-0 text-sm font-semibold uppercase tracking-wide text-gray-400">
                      Channel
                    </p>
                    <p className="mt-1 mb-0 truncate text-lg text-gray-600">
                      {currentChannelLabel || 'No channel'}
                    </p>
                  </div>
                  {/* <div className="border-t border-gray-200 pt-3">
                    <p className="m-0 text-sm font-semibold uppercase tracking-wide text-gray-400">
                      Sales person
                    </p>
                    <p className="mt-1 mb-0 truncate text-lg text-gray-600">
                      {currentSalesPersonLabel}
                    </p>
                  </div> */}
                </div>
              </div>
            </div>

            <div className="relative rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-4 shadow-sm shadow-blue-100/70">
              <div className="absolute -left-3 top-1/2 hidden size-6 -translate-y-1/2 items-center justify-center rounded-full border border-blue-100 bg-white text-blue-500 shadow-sm sm:flex">
                <ChevronRightIcon className="size-4" />
              </div>
              <div className="mb-4 flex items-center justify-between gap-3">
                <p className="m-0 text-base font-semibold uppercase tracking-wider text-blue-700">
                  New
                </p>
              </div>

              <div className="flex items-start gap-4">
                {sourceChannelDraft.source && (
                  <img
                    className="size-12 rounded-xl border border-white bg-white object-contain p-2 shadow-sm"
                    src={getReservationSourceImage(sourceChannelDraft.source)}
                    alt={selectedSource?.label || 'Selected source'}
                  />
                )}
                <div className="min-w-0 flex-1 space-y-3">
                  <div>
                    <p className="m-0 text-sm font-semibold uppercase tracking-wide text-blue-400">
                      Source
                    </p>
                    <p className="mt-1 mb-0 truncate text-xl font-gilroy-medium text-gray-900">
                      {selectedSource?.label || 'Select a source'}
                    </p>
                  </div>
                  <div className="border-t border-blue-100 pt-3">
                    <p className="m-0 text-sm font-semibold uppercase tracking-wide text-blue-400">
                      Channel
                    </p>
                    <p className="mt-1 mb-0 truncate text-lg text-blue-700">
                      {selectedChannel?.label || 'Select a channel'}
                    </p>
                  </div>
                  {/* <div className="border-t border-blue-100 pt-3">
                    <p className="m-0 text-sm font-semibold uppercase tracking-wide text-blue-400">
                      Sales person
                    </p>
                    <p className="mt-1 mb-0 truncate text-lg text-blue-700">
                      {selectedSalesPersonLabel}
                    </p>
                  </div> */}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="mb-5">
              <label
                htmlFor="reservation-segment"
                className="mb-2 block text-lg font-gilroy-medium text-gray-800"
              >
                Segment
              </label>
              <Select
                inputId="reservation-segment"
                className="react-select-container"
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={sourceChannelSelectMenuStyles}
                options={segmentOptions}
                value={segmentOptions.find((option) => option.value === draftTypeOfStay) || null}
                isDisabled={sourceChannelSaving || disableSourceChannelFields}
                placeholder="Select segment"
                onChange={(value: ValueType<SegmentOption, false>) => {
                  if (!value) return;

                  const nextTypeOfStay = value.value;
                  onDraftChange((draft) => {
                    // Sits above Source for the same reason as on the create
                    // form: BROKER narrows the sources, so a source picked
                    // beforehand has to be pulled back inside the new set.
                    const allowedSources = getAssignableSourceOptions(nextTypeOfStay);
                    const nextSource: ReservationSource =
                      draft.source &&
                      allowedSources.some((option) => option.value === draft.source)
                        ? draft.source
                        : allowedSources[0].value;
                    const nextChannelOptions = reservationChannelOptionsBySource[nextSource];
                    const channelStillAllowed = nextChannelOptions.some(
                      (option) => option.value === draft.channel
                    );

                    return {
                      ...draft,
                      typeOfStay: nextTypeOfStay,
                      source: nextSource,
                      channel: channelStillAllowed ? draft.channel : nextChannelOptions[0].value,
                      // A broker left over from a previous selection would
                      // credit a commission on a stay that is no longer a
                      // broker booking at all. server.service.property clears
                      // the stored value on its side for the same reason.
                      brokerId:
                        nextTypeOfStay === ReservationTypeOfStay.BROKER ? draft.brokerId : undefined
                    };
                  });
                }}
                aria-label="Reservation segment"
              />
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <label
                    htmlFor="reservation-source"
                    className="block text-lg font-gilroy-medium text-gray-800"
                  >
                    Source
                  </label>
                </div>
                <Select
                  inputId="reservation-source"
                  value={selectedSource}
                  options={sourceOptions}
                  isDisabled={sourceChannelSaving || disableSourceChannelFields}
                  placeholder="Select source"
                  onChange={(value: ValueType<ReservationSourceOption, false>) => {
                    if (!value) return;

                    const nextChannelOptions = reservationChannelOptionsBySource[value.value];
                    const channel = nextChannelOptions.some(
                      (option) => option.value === sourceChannelDraft.channel
                    )
                      ? sourceChannelDraft.channel
                      : nextChannelOptions[0].value;
                    onDraftChange((draft) => ({ ...draft, source: value.value, channel }));
                  }}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={sourceChannelSelectMenuStyles}
                  aria-label="Reservation source"
                />
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <label
                    htmlFor="reservation-channel"
                    className="block text-lg font-gilroy-medium text-gray-800"
                  >
                    Channel
                  </label>
                </div>
                <Select
                  inputId="reservation-channel"
                  value={selectedChannel}
                  options={availableChannelOptions}
                  isDisabled={
                    sourceChannelSaving || disableSourceChannelFields || !sourceChannelDraft.source
                  }
                  placeholder="Select channel"
                  onChange={(value: ValueType<ReservationChannelOption, false>) => {
                    if (value) {
                      onDraftChange((draft) => ({ ...draft, channel: value.value }));
                    }
                  }}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={sourceChannelSelectMenuStyles}
                  aria-label="Reservation channel"
                />
              </div>
            </div>

            <div className="mt-5">
              <label
                htmlFor="reservation-sales-person"
                className="mb-2 block text-lg font-gilroy-medium text-gray-800"
              >
                Sales person
              </label>
              <Select
                inputId="reservation-sales-person"
                className="react-select-container"
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={sourceChannelSelectMenuStyles}
                options={salesPersons.map((item) => ({
                  value: item.id || '',
                  label: item.name || item.email || item.phone || 'Unnamed sales person',
                  salesPerson: item
                }))}
                value={
                  selectedSalesPerson
                    ? ({
                        value: selectedSalesPerson.id || '',
                        label:
                          selectedSalesPerson.name ||
                          selectedSalesPerson.email ||
                          selectedSalesPerson.phone ||
                          'Unnamed sales person',
                        salesPerson: selectedSalesPerson
                      } as SalesPersonOption)
                    : null
                }
                onChange={(value: ValueType<SalesPersonOption, false>) => {
                  onDraftChange((draft) => ({
                    ...draft,
                    salesPerson: value?.salesPerson.email && value?.salesPerson.name
                      ? `${value.salesPerson.email} (${value.salesPerson.name})`
                      : ''
                  }));
                }}
                placeholder="Select sales person"
                isClearable={true}
                isLoading={isSalesPersonsLoading}
                isDisabled={sourceChannelSaving || isSalesPersonsLoading}
                noOptionsMessage={() =>
                  isSalesPersonsLoading ? 'Loading sales persons...' : 'No sales persons found'
                }
                aria-label="Reservation sales person"
              />
            </div>

            {isBrokerSegment && (
              <div className="mt-5">
                <label
                  htmlFor="reservation-broker"
                  className="mb-2 block text-lg font-gilroy-medium text-gray-800"
                >
                  Broker
                </label>
                <Select
                  inputId="reservation-broker"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={sourceChannelSelectMenuStyles}
                  options={brokerOptions}
                  value={selectedBroker || null}
                  onChange={(value: ValueType<BrokerOption, false>) => {
                    // Only the id is sent. server.service.property resolves the
                    // name from it and stores both, so a reservation can never
                    // name one broker while pointing at another.
                    onDraftChange((draft) => ({ ...draft, brokerId: value?.value }));
                  }}
                  placeholder="Select broker"
                  // Not clearable, unlike the create form. A cleared value is
                  // `undefined`, which JSON drops from the request body, so the
                  // save would silently keep the old broker -- a control that
                  // looks like it worked and did nothing. A BROKER stay should
                  // name a broker anyway; pick a different one to correct it.
                  isClearable={false}
                  isLoading={isBrokersLoading}
                  isDisabled={sourceChannelSaving || disableSourceChannelFields || isBrokersLoading}
                  noOptionsMessage={() =>
                    isBrokersLoading ? 'Loading brokers...' : 'No brokers found'
                  }
                  aria-label="Reservation broker"
                />
              </div>
            )}

            {isCxSource && (
              <>
                <div className="mt-5">
                  <label
                    htmlFor="reservation-marketing-channel"
                    className="mb-2 block text-lg font-gilroy-medium text-gray-800"
                  >
                    Marketing Channel
                  </label>
                  <Select
                    inputId="reservation-marketing-channel"
                    className="react-select-container"
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={sourceChannelSelectMenuStyles}
                    options={marketingChannelOptions}
                    value={selectedMarketingChannel}
                    onChange={(value: ValueType<AttributionOption<MarketingChannel>, false>) => {
                      onDraftChange((draft) => ({ ...draft, marketingChannel: value?.value }));
                    }}
                    placeholder="Where did the guest come from?"
                    // Same reason as the broker above: clearing would serialize
                    // to nothing and leave the saved value standing. `Unknown`
                    // is the in-enum way to say we could not tell, so nothing
                    // is lost by removing the clear.
                    isClearable={false}
                    isDisabled={sourceChannelSaving || disableSourceChannelFields}
                    aria-label="Reservation marketing channel"
                  />
                </div>

                <div className="mt-5">
                  <label
                    htmlFor="reservation-messaging-channel"
                    className="mb-2 block text-lg font-gilroy-medium text-gray-800"
                  >
                    Messaging Channel
                  </label>
                  <Select
                    inputId="reservation-messaging-channel"
                    className="react-select-container"
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={sourceChannelSelectMenuStyles}
                    options={messagingChannelOptions}
                    value={selectedMessagingChannel}
                    onChange={(value: ValueType<AttributionOption<MessagingChannel>, false>) => {
                      onDraftChange((draft) => ({ ...draft, messagingChannel: value?.value }));
                    }}
                    placeholder="How did the guest reach us?"
                    // `None` carries "the guest never messaged us", so the
                    // clear button would only ever be the no-op described above.
                    isClearable={false}
                    isDisabled={sourceChannelSaving || disableSourceChannelFields}
                    aria-label="Reservation messaging channel"
                  />
                </div>
              </>
            )}
          </div>

          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button
              type={ButtonType.gray}
              size={ButtonSize.small}
              className="w-full sm:w-auto"
              onClick={onClose}
              disabled={sourceChannelSaving}
            >
              Cancel
            </Button>
            <Button
              type={ButtonType.primary}
              size={ButtonSize.small}
              className="w-full sm:w-auto"
              loading={sourceChannelSaving}
              disabled={
                !sourceChannelDraft.source ||
                !sourceChannelDraft.channel ||
                !hasSourceChannelChanges
              }
              onClick={onSave}
            >
              Save changes
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
